import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import {
  authStateReducer,
  createInitialAuthState,
} from "@/contexts/authState";
import { setUnauthorizedHandler } from "@/services/apiClient";
import {
  clearStoredAuthTokens,
  getStoredAccessToken,
  storeTokenResponse,
} from "@/services/authTokenStorage";
import { isRequestCanceled } from "@/services/apiErrors";
import { clearUiComponents } from "@/storage/uiComponentsRepository";
import { clearUiSidebarItems } from "@/storage/uiSidebarItemsRepository";
import {
  getMeProfile,
  introspectAuthSession,
  loginUser,
  logoutUser,
  type LoginRequestOptions,
} from "@/services/iamApi";
import type { User } from "@/types";

export interface AuthSession {
  token: string | null;
  user: User | null;
  projectId: string | null;
  permissions: string[];
  isAuthenticated: boolean;
  loading: boolean;
  login: (
    username: string,
    password: string,
    options?: LoginRequestOptions,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

export function useAuthSession(): AuthSession {
  const [state, dispatch] = useReducer(
    authStateReducer,
    getStoredAccessToken(),
    createInitialAuthState,
  );

  const clearBrowserData = useCallback(async () => {
    await Promise.all([
      clearUiComponents(),
      clearUiSidebarItems(),
    ]);
  }, []);

  const clearSession = useCallback(() => {
    clearStoredAuthTokens();
    dispatch({ type: "sessionCleared" });
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const profile = await getMeProfile();
      let permissions: string[] = [];
      try {
        permissions = (await introspectAuthSession()).permissions;
      } catch {
        // Profile loading remains sufficient to render the authenticated app.
      }
      dispatch({
        type: "profileLoaded",
        user: profile.user,
        projectId: profile.projectId,
        permissions,
      });
    } catch {
      clearSession();
    }
  }, [clearSession]);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    if (!state.token) {
      dispatch({ type: "sessionCleared" });
      return;
    }

    const profileLoadAbortController = new AbortController();
    dispatch({ type: "profileLoadStarted" });

    (async () => {
      try {
        const profile = await getMeProfile({
          signal: profileLoadAbortController.signal,
        });
        let permissions: string[] = [];
        try {
          permissions = (await introspectAuthSession({
            signal: profileLoadAbortController.signal,
          })).permissions;
        } catch (error) {
          if (
            profileLoadAbortController.signal.aborted
            || isRequestCanceled(error)
          ) throw error;
          // Profile loading remains sufficient to render the authenticated app.
        }
        dispatch({
          type: "profileLoaded",
          user: profile.user,
          projectId: profile.projectId,
          permissions,
        });
      } catch (error) {
        if (
          profileLoadAbortController.signal.aborted
          || isRequestCanceled(error)
        ) return;

        clearSession();
      }
    })();

    return () => {
      profileLoadAbortController.abort();
    };
  }, [state.token, clearSession]);

  const login = useCallback(async (
    username: string,
    password: string,
    options: LoginRequestOptions = {},
  ) => {
    const tokenResp = await loginUser(username, password, options);
    if (options.signal?.aborted) return;

    const token = storeTokenResponse(tokenResp);
    dispatch({ type: "sessionStarted", token });
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch {
      // Server logout is best-effort; local session cleanup owns the UX.
    } finally {
      clearSession();
      void clearBrowserData().catch(() => {
        // Explicit logout cleanup is best-effort.
      });
    }
  }, [clearSession, clearBrowserData]);

  return useMemo<AuthSession>(
    () => ({
      token: state.token,
      user: state.user,
      projectId: state.projectId,
      permissions: state.permissions ?? [],
      isAuthenticated: Boolean(state.token),
      loading: state.loading,
      login,
      logout,
      refreshMe,
    }),
    [state, login, logout, refreshMe],
  );
}
